import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { 
  Award, 
  Calendar, 
  TrendingUp, 
  FileText,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface ShiftaskEvaluationModalProps {
  guardId: string;
  guardName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ShiftaskEvaluationModal: React.FC<ShiftaskEvaluationModalProps> = ({
  guardId,
  guardName,
  isOpen,
  onClose
}) => {
  // Fetch all Shiftask evaluations for this guard
  const { data: shiftaskEvaluations = [], isLoading } = useQuery({
    queryKey: ['shiftask-evaluations-all', guardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ovit_evaluations')
        .select('*')
        .eq('guard_id', guardId)
        .order('evaluation_date', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: isOpen && !!guardId,
  });

  const activeEvaluation = shiftaskEvaluations.find(evaluation => evaluation.status === 'active');
  const currentYear = new Date().getFullYear();
  const currentSemester = new Date().getMonth() >= 6 ? 'H2' : 'H1';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4" />;
      case 'expired':
        return <AlertCircle className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getSemesterLabel = (period: string) => {
    return period === 'H1' ? 'Α΄ Εξάμηνο' : 'Β΄ Εξάμηνο';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-blue-600" />
            Εσωτερική Αξιολόγηση (Shiftask) - {guardName}
          </DialogTitle>
          <DialogDescription>
            Ιστορικό και λεπτομέρειες εσωτερικών αξιολογήσεων και εξαμηνιαίων βαθμολογήσεων
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : shiftaskEvaluations.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Δεν υπάρχουν αξιολογήσεις Shiftask
            </h3>
            <p className="text-gray-600">
              Δεν έχουν καταχωρηθεί ακόμη αξιολογήσεις Shiftask για αυτόν τον υπάλληλο.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Active Evaluation */}
            {activeEvaluation && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="h-5 w-5" />
                    {t("activeEvaluation") || "Active Evaluation"}
                  </CardTitle>
                  <CardDescription className="text-green-700">
                    {t("currentEvaluationForPerformance") || "Current evaluation used for performance calculation"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <div className="text-3xl font-bold text-green-800">
                          {activeEvaluation.overall_score}%
                        </div>
                        <div className="text-sm text-green-600">
                          Συνολικό Σκορ ({(activeEvaluation.overall_score * 0.8).toFixed(0)} πόντοι βάσης)
                        </div>
                      </div>
                      <Progress 
                        value={activeEvaluation.overall_score} 
                        className="h-3"
                      />
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Περίοδος:</span>
                        <Badge variant="outline">
                          {getSemesterLabel(activeEvaluation.evaluation_period)} {activeEvaluation.evaluation_year}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Ημερομηνία:</span>
                        <span className="text-sm">
                          {format(new Date(activeEvaluation.evaluation_date), 'dd/MM/yyyy', { locale: el })}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Ισχύει έως:</span>
                        <span className="text-sm">
                          {activeEvaluation.valid_until ? 
                            format(new Date(activeEvaluation.valid_until), 'dd/MM/yyyy', { locale: el }) : 
                            'Απεριόριστα'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  {activeEvaluation.notes && (
                    <div className="mt-4 p-3 bg-white rounded-lg border">
                      <div className="text-sm font-medium mb-1">Σημειώσεις:</div>
                      <div className="text-sm text-gray-600">{activeEvaluation.notes}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Evaluation History */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold">Ιστορικό Αξιολογήσεων</h3>
              </div>

              {/* Group evaluations by year */}
              {Object.entries(
                shiftaskEvaluations.reduce((acc, evaluation) => {
                  const year = evaluation.evaluation_year;
                  if (!acc[year]) acc[year] = [];
                  acc[year].push(evaluation);
                  return acc;
                }, {} as Record<number, typeof shiftaskEvaluations>)
              )
              .sort(([a], [b]) => Number(b) - Number(a))
              .map(([year, yearEvaluations]: [string, any[]]) => (
                <Card key={year}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Έτος {year}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {yearEvaluations
                        .sort((a, b) => a.evaluation_period.localeCompare(b.evaluation_period))
                        .map((evaluation) => (
                        <div 
                          key={evaluation.id}
                          className={`p-4 rounded-lg border ${
                            evaluation.status === 'active' ? 'border-green-200 bg-green-50' : 'border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(evaluation.status)}
                              <span className="font-medium">
                                {getSemesterLabel(evaluation.evaluation_period)}
                              </span>
                            </div>
                            <Badge className={getStatusColor(evaluation.status)}>
                              {evaluation.status === 'active' ? t("active") || "Active" : 
                               evaluation.status === 'expired' ? t("expired") || "Expired" : 
                               evaluation.status === 'pending' ? t("pending") || "Pending" : evaluation.status}
                            </Badge>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Σκορ:</span>
                              <span className="font-bold text-lg">
                                {evaluation.overall_score}%
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Βάση:</span>
                              <span className="text-sm font-medium">
                                {(evaluation.overall_score * 0.8).toFixed(0)} πόντοι
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Ημερομηνία:</span>
                              <span className="text-sm">
                                {format(new Date(evaluation.evaluation_date), 'dd/MM/yyyy', { locale: el })}
                              </span>
                            </div>
                            <Progress 
                              value={evaluation.overall_score} 
                              className="h-2 mt-2"
                            />
                          </div>
                          
                          {evaluation.notes && (
                            <div className="mt-3 p-2 bg-white rounded border text-xs text-gray-600">
                              {evaluation.notes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Performance Impact */}
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <TrendingUp className="h-5 w-5" />
                  Επίδραση στην Απόδοση
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {activeEvaluation ? (activeEvaluation.overall_score * 0.8).toFixed(0) : '0'}
                    </div>
                    <div className="text-sm text-blue-700">Πόντοι Βάσης</div>
                    <div className="text-xs text-blue-600 mt-1">
                      {activeEvaluation ? `από ${activeEvaluation.overall_score}% Shiftask` : 'Χωρίς Αξιολόγηση'}
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {currentSemester}
                    </div>
                    <div className="text-sm text-blue-700">Τρέχον Εξάμηνο</div>
                    <div className="text-xs text-blue-600 mt-1">{currentYear}</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {shiftaskEvaluations.length}
                    </div>
                    <div className="text-sm text-blue-700">Συνολικές Αξιολογήσεις</div>
                    <div className="text-xs text-blue-600 mt-1">Όλων των εποχών</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};